/**
 * Photo Reviews section (Loox-style cards)
 * - Handles the lightbox for the static (theme-editor) markup.
 * - When a Judge.me public token is set: fetches ALL pages of THIS
 *   PRODUCT's reviews up front (via product_review, scoped by external_id,
 *   looping on total_pages), sorts photo/video reviews first, caches the
 *   combined list in sessionStorage (per shop + product, cleared when the
 *   tab closes), and reveals them in chunks via "Load more" — no repeat
 *   network calls once loaded.
 * - Without a token, the static placeholder blocks are shown as-is with the
 *   same reveal-based "Load more".
 * - Each card is a single clickable unit (whole card, not a sub-button) —
 *   click or Enter/Space opens the review detail lightbox.
 *
 * SECURITY: cards and the lightbox are built as real DOM nodes with review
 * text assigned via `textContent`, never `innerHTML`. Judge.me review body,
 * title, and reviewer name are user-submitted content — concatenating them
 * into an HTML string lets a review containing e.g. "<img src=x
 * onerror=...>" execute in every visitor's browser. textContent never
 * interprets its input as markup, so this class of stored-XSS is closed
 * regardless of what a review contains.
 */
(function () {
  var REVEAL_INITIAL = 8;
  var REVEAL_STEP = 4;
  var SESSION_KEY_PREFIX = 'pr_reviews_';

  function initSection(sectionEl) {
    var track = sectionEl.querySelector('.pr-masonry');
    var lightbox = sectionEl.querySelector('.pr-lightbox');
    var lightboxContent = lightbox ? lightbox.querySelector('.pr-lightbox-content') : null;
    var lightboxClose = lightbox ? lightbox.querySelector('.pr-lightbox-close') : null;
    var loadMoreBtn = sectionEl.querySelector('.pr-load-more');

    if (!track) return;

    function handleCardActivate(card) {
      if (!lightbox || !lightboxContent) return;
      if (card._reviewData) {
        openRichLightbox(lightbox, lightboxContent, card._reviewData);
      } else {
        var img = card.querySelector('img');
        if (!img) return; // text-only static placeholder card — nothing to enlarge
        openSimpleLightbox(lightbox, lightboxContent, img.src);
      }
    }

    // Whole card is clickable (Loox-style — no separate "expand" button).
    // - Live cards carry the full review object as a JS property on the
    //   <li> itself (see buildCardElement) — open the rich modal.
    // - Static theme-editor placeholder cards have no such property — fall
    //   back to the simple enlarged-photo view.
    track.addEventListener('click', function (e) {
      var card = e.target.closest('.pr-card');
      if (!card) return;
      handleCardActivate(card);
    });

    // Keyboard accessibility: cards are focusable (tabindex="0",
    // role="button") since the whole card is now the interactive element.
    track.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest('.pr-card');
      if (!card) return;
      e.preventDefault();
      handleCardActivate(card);
    });

    if (lightboxClose) {
      lightboxClose.addEventListener('click', function () {
        lightbox.hidden = true;
      });
    }
    if (lightbox) {
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox) lightbox.hidden = true;
      });
    }

    var shopDomain = sectionEl.dataset.shopDomain;
    var publicToken = sectionEl.dataset.judgemeToken;
    var productId = sectionEl.dataset.productId;

    if (publicToken) {
      initLiveReviews(track, loadMoreBtn, shopDomain, publicToken, productId);
    } else {
      revealInDom(track, loadMoreBtn); // static placeholder cards already in the DOM
    }
  }

  /**
   * Resolve which product a review belongs to, for the product row shown on
   * both the card and the rich modal. Field names for per-review product
   * info aren't confirmed from Judge.me's docs — trying a few likely
   * shapes with fallback. Log a review object in the console and adjust
   * these lookups if your store's response nests it differently.
   */
  function getProductInfo(review) {
    var title = review.product_title || null;
    var url = review.product_url_with_utm || review.product_url || null;
    var image = review.product_pictures_url || review.product_variant_image_url || null;

    return { title: title, url: url, image: image };
  }

  /**
   * Simple mode: just the enlarged photo — used when a card has no full
   * review data attached (the static theme-editor placeholder blocks).
   */
  function openSimpleLightbox(lightbox, lightboxContent, photoSrc) {
    lightbox.classList.remove('pr-lightbox--rich');
    lightboxContent.className = 'pr-lightbox-content pr-lightbox-content--simple';
    lightboxContent.innerHTML = '';

    var img = document.createElement('img');
    img.src = photoSrc; // assigning to .src never executes markup
    img.alt = '';
    lightboxContent.appendChild(img);

    lightbox.hidden = false;
  }

  /**
   * Rich mode: Judge.me-style two-pane review modal — photo on the left,
   * reviewer name / verified badge / stars / full text / linked product
   * card on the right. Built entirely from DOM nodes with review text
   * assigned via `textContent` (see the SECURITY note at the top of this
   * file — review-submitted text must never go through innerHTML).
   */
  function openRichLightbox(lightbox, lightboxContent, review) {
    console.log('[PhotoReviews] opening rich lightbox for review:', review);
    console.log('[PhotoReviews] review object keys:', Object.keys(review));

    lightbox.classList.add('pr-lightbox--rich');
    lightboxContent.className = 'pr-lightbox-content pr-lightbox-content--rich';
    lightboxContent.innerHTML = '';

    var photoUrl =
      review.pictures_urls && review.pictures_urls.length
        ? review.pictures_urls[0] && review.pictures_urls[0].original
        : null;

    // Photo pane
    var photoPane = document.createElement('div');
    photoPane.className = 'pr-modal-photo-pane';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pr-modal-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', function () {
      lightbox.hidden = true;
    });
    photoPane.appendChild(closeBtn);

    if (photoUrl) {
      var photoImg = document.createElement('img');
      photoImg.src = photoUrl;
      photoImg.alt = '';
      photoPane.appendChild(photoImg);
    }

    // Detail pane
    var detailPane = document.createElement('div');
    detailPane.className = 'pr-modal-detail-pane';

    var header = document.createElement('div');
    header.className = 'pr-modal-header';

    var name = document.createElement('p');
    name.className = 'pr-modal-name';
    name.textContent = review.reviewer_name || 'Customer'; // review-submitted — textContent only
    header.appendChild(name);

    if (review.verified_buyer) {
      var verified = document.createElement('span');
      verified.className = 'pr-modal-verified';
      verified.insertAdjacentHTML(
        'afterbegin',
        '<svg class="pr-modal-verified-icon" viewBox="0 0 16 16"><path d="M8 0a8 8 0 108 8 8 8 0 00-8-8zM6.5 11.5L3 8l1.1-1.1 2.4 2.4L11.9 4l1.1 1.1z"/></svg>'
      );
      // ^ safe: fixed SVG template, no review data interpolated into it.
      verified.appendChild(document.createTextNode('Verified'));
      header.appendChild(verified);
    }

    detailPane.appendChild(header);

    var starsWrap = document.createElement('div');
    starsWrap.className = 'pr-modal-stars';
    starsWrap.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 5; i++) {
      var filled = i < (review.rating || 0);
      starsWrap.insertAdjacentHTML(
        'beforeend',
        '<svg class="pr-star" style="width:18px;height:18px" viewBox="0 0 20 20"><path class="' +
          (filled ? 'pr-star--full' : 'pr-star--empty') +
          '" d="M10 1.6l2.6 5.6 6 .7-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L1.4 7.9l6-.7L10 1.6z"/></svg>'
      );
    }
    detailPane.appendChild(starsWrap);

    var body = document.createElement('div');
    body.className = 'pr-modal-body';

    var textEl = document.createElement('p');
    textEl.className = 'pr-modal-text';
    var fullText = review.title ? review.title + '\n\n' + (review.body || '') : review.body || '';
    textEl.textContent = fullText; // review-submitted — textContent only
    body.appendChild(textEl);

    detailPane.appendChild(body);

    var productInfo = getProductInfo(review);
    console.log(
      '[PhotoReviews] product field resolution — title:', productInfo.title,
      '| url:', productInfo.url, '| image:', productInfo.image
    );
    // review.product doesn't exist on the real response shape — fields are
    // flat (product_title, product_pictures_url, etc.), confirmed against
    // an actual Judge.me review object.
    if (!productInfo.title) {
      console.warn('[PhotoReviews] no product title found on this review — the "View product" card will be skipped. Check the raw review object above for the actual field name.');
    }

    if (productInfo.title) {
      var productCard = document.createElement('div');
      productCard.className = 'pr-modal-product';

      if (productInfo.image) {
        var productImg = document.createElement('img');
        productImg.className = 'pr-modal-product-img';
        productImg.src = productInfo.image;
        productImg.alt = '';
        productCard.appendChild(productImg);
      }

      var productInfoEl = document.createElement('div');
      productInfoEl.className = 'pr-modal-product-info';

      var productName = document.createElement('p');
      productName.className = 'pr-modal-product-name';
      productName.textContent = productInfo.title; // review-submitted/product data — textContent only
      productInfoEl.appendChild(productName);

      var productLinkHref = productInfo.url;
      if (productLinkHref) {
        var productLink = document.createElement('a');
        productLink.className = 'pr-modal-product-link';
        productLink.href = productLinkHref; // full URL from Judge.me (already has UTM params) when available
        productLink.insertAdjacentHTML(
          'afterbegin',
          '<svg viewBox="0 0 20 20"><path d="M6 7V5a4 4 0 118 0v2M4 7h12l-1 11H5L4 7z"/></svg>'
        );
        // ^ safe: fixed SVG template.
        productLink.appendChild(document.createTextNode('View product'));
        productInfoEl.appendChild(productLink);
      }

      productCard.appendChild(productInfoEl);
      detailPane.appendChild(productCard);
    }

    lightboxContent.appendChild(photoPane);
    lightboxContent.appendChild(detailPane);

    lightbox.hidden = false;
  }

  /**
   * Generic "reveal N at a time" behavior shared by both static placeholder
   * cards and the fully-loaded live review set — both cases end up with
   * every card already in the DOM, just hidden past REVEAL_INITIAL.
   */
  function revealInDom(track, loadMoreBtn) {
    function setup() {
      var cards = Array.prototype.slice.call(track.querySelectorAll('.pr-card'));
      cards.forEach(function (card, i) {
        card.hidden = i >= REVEAL_INITIAL;
      });
      if (!loadMoreBtn) return;
      loadMoreBtn.hidden = cards.length <= REVEAL_INITIAL;
    }

    if (loadMoreBtn && !loadMoreBtn.dataset.revealBound) {
      loadMoreBtn.dataset.revealBound = 'true';
      loadMoreBtn.addEventListener('click', function () {
        var hiddenCards = Array.prototype.slice
          .call(track.querySelectorAll('.pr-card[hidden]'))
          .slice(0, REVEAL_STEP);
        hiddenCards.forEach(function (card) {
          card.hidden = false;
        });
        if (!track.querySelector('.pr-card[hidden]')) {
          loadMoreBtn.hidden = true;
        }
      });
    }

    setup();
  }

  /**
   * Live Judge.me data: use the sessionStorage cache if present, otherwise
   * fetch every page (1..total_pages) for this product, sort, cache, then
   * render + reveal.
   */
  function initLiveReviews(track, loadMoreBtn, shopDomain, publicToken, productId) {
    console.log('[PhotoReviews] init — shopDomain:', shopDomain, '| productId:', productId, '| token set:', !!publicToken);

    var cacheKey = SESSION_KEY_PREFIX + shopDomain + '_' + productId;
    var cached = readCache(cacheKey);

    if (cached) {
      console.log('[PhotoReviews] using cached reviews from sessionStorage —', cached.length, 'reviews. Key:', cacheKey);
      console.log('[PhotoReviews] cached reviews:', cached);
      renderReviews(track, cached, 'replace');
      revealInDom(track, loadMoreBtn);
      return;
    }

    console.log('[PhotoReviews] no cache found for key', cacheKey, '— fetching from Judge.me.');
    if (loadMoreBtn) loadMoreBtn.hidden = true; // nothing to reveal until the fetch finishes

    fetchProductReviews(shopDomain, publicToken, productId)
      .then(function (reviews) {
        console.log('[PhotoReviews] fetched', reviews.length, 'total reviews across all pages.');
        var sorted = sortByImageAvailability(reviews);
        console.log('[PhotoReviews] sorted reviews (image reviews first):', sorted);
        writeCache(cacheKey, sorted);
        renderReviews(track, sorted, 'replace');
        revealInDom(track, loadMoreBtn);
      })
      .catch(function (err) {
        console.error('[PhotoReviews] Judge.me fetch failed, showing placeholder reviews.', err);
        revealInDom(track, loadMoreBtn); // fall back to whatever placeholder cards exist
      });
  }

  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn('[PhotoReviews] could not read review cache.', err);
      return null;
    }
  }

  function writeCache(key, reviews) {
    try {
      sessionStorage.setItem(key, JSON.stringify(reviews));
      console.log('[PhotoReviews] cached', reviews.length, 'reviews under sessionStorage key:', key);
    } catch (err) {
      // Quota exceeded or storage disabled — fine to skip caching silently.
      console.warn('[PhotoReviews] could not cache reviews in sessionStorage.', err);
    }
  }

  /**
   * Reviews with at least one photo/video sort first; order within each
   * group is otherwise left as returned by the API (stable sort).
   */
  function sortByImageAvailability(reviews) {
    var sorted = reviews.slice().sort(function (a, b) {
      var aHasImage = a.pictures_urls && a.pictures_urls.length ? 1 : 0;
      var bHasImage = b.pictures_urls && b.pictures_urls.length ? 1 : 0;
      return bHasImage - aHasImage;
    });
    var withImageCount = sorted.filter(function (r) {
      return r.pictures_urls && r.pictures_urls.length;
    }).length;
    console.log('[PhotoReviews] sort complete —', withImageCount, 'of', sorted.length, 'reviews have at least one photo.');
    return sorted;
  }

  /**
   * Fetch every page of this product's reviews from Judge.me's
   * "Product Review" Widget API, following total_pages until exhausted.
   *
   * The Widget API (unlike /api/v1/reviews) is designed to be called from
   * public JS with the PUBLIC token — Judge.me's own docs confirm this, so
   * no server-side proxy is needed here. Never put the PRIVATE token in
   * theme JS; only the public one is safe client-side.
   *
   * GET https://judge.me/api/v1/widgets/product_review
   *   ?shop_domain=your-store.myshopify.com
   *   &api_token=YOUR_PUBLIC_TOKEN
   *   &external_id=PRODUCT_ID       (Shopify product.id)
   *   &page=N
   *   &json_request=true
   *
   * NOTE: if this store has Judge.me "Product Groups" enabled, the response
   * for a single external_id can include reviews pooled from every product
   * in that product's group, not just this exact product — confirmed
   * against real data. That's expected Judge.me behavior, not a bug here.
   *
   * If your account's response nests `reviews`/`total_pages` differently
   * (e.g. under `widget`), adjust the lookups below — log `data` once on
   * page 1 to confirm the real shape for your store.
   */
  async function fetchProductReviews(shopDomain, publicToken, productId) {
    if (!shopDomain || !publicToken) {
      throw new Error('Missing shop domain or Judge.me public token.');
    }
    if (!productId) {
      throw new Error('Missing product id (external_id) for product_review.');
    }

    console.log('[PhotoReviews] fetching page 1 to learn total_pages...');
    var first = await fetchPage(shopDomain, publicToken, productId, 1);
    var allReviews = first.reviews.slice();
    var totalPages = first.totalPages;
    console.log('[PhotoReviews] total_pages reported:', totalPages, '| reviews on page 1:', first.reviews.length);

    if (totalPages > 1) {
      var pageNumbers = [];
      for (var p = 2; p <= totalPages; p++) pageNumbers.push(p);
      console.log('[PhotoReviews] fetching remaining pages in parallel:', pageNumbers);

      var rest = await Promise.all(
        pageNumbers.map(function (p) {
          return fetchPage(shopDomain, publicToken, productId, p);
        })
      );
      rest.forEach(function (result, i) {
        console.log('[PhotoReviews] page', pageNumbers[i], 'returned', result.reviews.length, 'reviews.');
        allReviews = allReviews.concat(result.reviews);
      });
    }

    console.log('[PhotoReviews] total reviews across all pages:', allReviews.length);
    return allReviews;
  }

  async function fetchPage(shopDomain, publicToken, productId, page) {
    var params = new URLSearchParams({
      shop_domain: shopDomain,
      api_token: publicToken,
      external_id: productId,
      page: page,
      json_request: 'true'
    });
    var url = 'https://judge.me/api/v1/widgets/product_review?' + params.toString();
    console.log('[PhotoReviews] GET', url);

    var res = await fetch(url);
    if (!res.ok) throw new Error('Judge.me request failed on page ' + page + ': ' + res.status);
    var data = await res.json();

    console.log('[PhotoReviews] raw response for page', page, '— full object:', data);
    console.log('[PhotoReviews] top-level keys on page', page, ':', Object.keys(data));

    var reviews = data.reviews || (data.widget && data.widget.reviews) || [];
    var totalPages = data.total_pages || (data.widget && data.widget.total_pages) || 1;

    if (reviews.length > 0) {
      console.log('[PhotoReviews] keys on a single review object (page ' + page + '):', Object.keys(reviews[0]));
      console.log('[PhotoReviews] first review on page', page, ':', reviews[0]);
    } else {
      console.warn('[PhotoReviews] page', page, 'returned 0 reviews — check the `reviews` lookup above matches your response shape.');
    }

    return { reviews: reviews, totalPages: totalPages };
  }

  /**
   * Build one review's <li class="pr-card"> as real DOM nodes.
   * Layout (top to bottom): photo (+ "N extra photos" badge) → reviewer
   * name + verified checkmark → star rating → optional title → review text
   * (clamped via CSS) → linked product row.
   *
   * The whole <li> is the click target (see initSection) — role="button"
   * and tabindex make it keyboard-accessible too.
   *
   * Every piece of review-submitted text (title, body, reviewer name) goes
   * in via `textContent`, not string concatenation into innerHTML — see the
   * SECURITY note at the top of this file for why that distinction matters.
   */
  function buildCardElement(review) {
    var li = document.createElement('li');
    li.className = 'pr-card';
    li.tabIndex = 0;
    li.setAttribute('role', 'button');
    li.setAttribute('aria-label', 'View full review from ' + (review.reviewer_name || 'a customer'));
    li._reviewData = review; // JS property, not an HTML attribute — read by the click/keydown handlers in initSection

    var photoUrl =
      review.pictures_urls && review.pictures_urls.length
        ? review.pictures_urls[0] && review.pictures_urls[0].original
        : null;
    var extraPhotoCount = review.pictures_urls ? review.pictures_urls.length - 1 : 0;

    if (photoUrl) {
      var media = document.createElement('div');
      media.className = 'pr-card-media';

      var img = document.createElement('img');
      img.className = 'pr-card-img';
      img.loading = 'lazy';
      img.alt = '';
      img.src = photoUrl; // assigning to .src (not innerHTML) never executes markup
      media.appendChild(img);

      if (extraPhotoCount > 0) {
        var badge = document.createElement('span');
        badge.className = 'pr-card-badge';
        badge.insertAdjacentHTML(
          'afterbegin',
          '<svg viewBox="0 0 20 20"><path d="M4 4h10v10H4V4zm2 12h10V6h2v12H6v-2z"/></svg>'
        );
        // ^ safe: fixed SVG template, no review data interpolated into it.
        badge.appendChild(document.createTextNode('+' + extraPhotoCount));
        media.appendChild(badge);
      }

      li.appendChild(media);
    }

    var body = document.createElement('div');
    body.className = 'pr-card-body';

    var nameRow = document.createElement('div');
    nameRow.className = 'pr-card-name-row';

    var author = document.createElement('span');
    author.className = 'pr-card-author';
    author.textContent = review.reviewer_name || 'Customer'; // review-submitted text — textContent only
    nameRow.appendChild(author);

    if (review.verified_buyer) {
      nameRow.insertAdjacentHTML(
        'beforeend',
        '<svg class="pr-card-verified-icon" viewBox="0 0 16 16" aria-label="Verified buyer"><path d="M8 0a8 8 0 108 8 8 8 0 00-8-8zM6.5 11.5L3 8l1.1-1.1 2.4 2.4L11.9 4l1.1 1.1z"/></svg>'
      );
      // ^ safe: fixed SVG template, no review data interpolated into it.
    }
    body.appendChild(nameRow);

    var starsWrap = document.createElement('div');
    starsWrap.className = 'pr-card-stars';
    starsWrap.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 5; i++) {
      var filled = i < (review.rating || 0);
      starsWrap.insertAdjacentHTML(
        'beforeend',
        '<svg class="pr-star pr-star--sm ' +
          (filled ? 'pr-star--full' : 'pr-star--empty') +
          '" viewBox="0 0 20 20"><path d="M10 1.6l2.6 5.6 6 .7-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L1.4 7.9l6-.7L10 1.6z"/></svg>'
      );
    }
    body.appendChild(starsWrap);
    // ^ safe: this markup is a fixed SVG template with no review data interpolated into it.

    if (review.title) {
      var titleEl = document.createElement('p');
      titleEl.className = 'pr-card-title';
      titleEl.textContent = review.title; // review-submitted text — textContent only
      body.appendChild(titleEl);
    }

    var textEl = document.createElement('p');
    textEl.className = 'pr-card-text';
    textEl.textContent = review.body || ''; // review-submitted text — textContent only
    body.appendChild(textEl);

    var productInfo = getProductInfo(review);
    if (productInfo.title) {
      var productRow = document.createElement('div');
      productRow.className = 'pr-card-product';

      if (productInfo.image) {
        var productImg = document.createElement('img');
        productImg.className = 'pr-card-product-thumb';
        productImg.src = productInfo.image;
        productImg.alt = '';
        productRow.appendChild(productImg);
      }

      var productName = document.createElement('span');
      productName.className = 'pr-card-product-name';
      productName.textContent = productInfo.title; // review-submitted/product data — textContent only
      productRow.appendChild(productName);

      body.appendChild(productRow);
    }

    li.appendChild(body);

    return li;
  }

  /**
   * Render review cards into `track`.
   * mode: 'replace' clears existing content first; 'append' adds to the end.
   */
  function renderReviews(track, reviews, mode) {
    console.log('[PhotoReviews] rendering', reviews.length, 'review cards, mode:', mode);
    if (mode !== 'append') {
      track.innerHTML = '';
    }
    var fragment = document.createDocumentFragment();
    reviews.forEach(function (review) {
      fragment.appendChild(buildCardElement(review));
    });
    track.appendChild(fragment);
  }

  window.PhotoReviews = {
    init: function (sectionId) {
      var el = document.getElementById(sectionId);
      if (el) initSection(el);
    },
    fetchProductReviews: fetchProductReviews,
    renderReviews: renderReviews
  };
})();